import React, { useState, useEffect } from 'react';
import QuestionCard from '../components/QuestionCard';
import { fetchValidQuestions, submitAnswers, startQuiz } from '../api/api';
import ProfilePicture from '../components/ProfilePicture';
import ThemeToggle from '../components/ThemeSwitcher/ThemeSwitcher';
import { useTheme } from '../context/ThemeContext';
import { useParams } from "react-router-dom";
import QuizInformation from '../components/QuizInformation';
import LearnerLogin from '../components/LearnerLogin';
import ProgressBar from '../components/ProgressBar';
import { useNavigate } from "react-router-dom";
import LoaderModal from '../components/LoaderModal';

const Quiz = () => {
    const { themeStyles } = useTheme();
    const { quizId } = useParams();
    const navigate = useNavigate();

    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [viewMode, setViewMode] = useState('single'); // 'single' or 'list'
    const [timeLeft, setTimeLeft] = useState(600); // 10 minutes timer in seconds
    const [quizStarted, setQuizStarted] = useState(false);
    const [quizDetails, setQuizDetails] = useState({});
    const [loginModal, setLoginModal] = useState(false);
    const [learner, setLearner] = useState({});
    const [sessionId, setSessionId] = useState(null);
    const [quizProgress, setQuizProgress] = useState(0);
    const [isMenuVisible, setMenuVisible] = useState(false);

    // Fetch questions from backend
    useEffect(() => {
        const fetchQuestions = async () => {
            const data = await fetchValidQuestions(quizId);
            setQuestions(data.questions);
            setQuizDetails(data.quizDetails)
            setTimeLeft(data?.quizDetails?.timeLimit * 60)
        };
        fetchQuestions();
        if (!localStorage.getItem("learnerId")) {
            setLoginModal(true)
        }
    }, [quizId]);

    // Timer countdown logic
    useEffect(() => {
        if (quizStarted) {
            if (timeLeft > 0) {
                const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
                return () => clearInterval(timer);
            }
        }
    }, [timeLeft, quizStarted]);

    const handleSelect = async (option, questionId) => {
        setAnswers({ ...answers, [questionId]: option });

        try {
            await submitAnswers({ data: [{ sessionId, questionId, answer: option }] });
        } catch (error) {
            alert("something went  wrong");
        }

    };

    const handleQuizStart = async () => {
        try {
            const response = await startQuiz({ quizId: quizId, learnerId: learner.id ?? localStorage.getItem("learnerId") });
            setSessionId(response.sessionId);
            setQuizStarted(true);
        } catch (error) {
            alert("Something went wrong, Please try again!")
        }

    }

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const onLogin = (response) => {
        localStorage.setItem("learnerToken", response.token);
        localStorage.setItem("learnerId", response.learner.id);
        localStorage.setItem("learnerName", response.learner.name);
        setLoginModal(false);
        setLearner(response.learner)
    }

    const handleQuizProgress = (totalQuestions, answeredQuestions) => {
        if (totalQuestions === 0) return 0; // Avoid division by zero
        let progress = (answeredQuestions / totalQuestions) * 100;
        progress = Math.min(progress, 100); // Ensure progress doesn't exceed 100%
        setQuizProgress(progress)
    };

    useEffect(() => { handleQuizProgress(questions.length, Object.keys(answers).length) }, [answers])


    if (!questions?.length) return <LoaderModal />;

    return (
        <div className="h-screen flex flex-col"
            style={{
                background: themeStyles.background,
                color: themeStyles.text,
            }}>

            {loginModal && <LearnerLogin onLogin={onLogin} />}


            {/* Fixed Header */}
            <div>
                {/* Top Bar for larger screens */}
                <div className="w-full bg-custom-blue text-white p-4 flex items-center relative fixed top-0 left-0 z-10 hidden md:flex">
                    {/* Left: Profile Picture */}
                    <div className="flex-shrink-0">
                        <ProfilePicture quiz={true} learnerName={learner.name ?? localStorage.getItem("learnerName")} />
                    </div>

                    {/* Center: Timer */}
                    {quizStarted && (
                        <h2 className="absolute left-1/2 transform -translate-x-1/2 text-2xl font-bold">
                            Time Left: {formatTime(timeLeft)}
                        </h2>
                    )}

                    {/* Right: Button and ThemeToggle */}
                    <div className="ml-auto flex items-center space-x-4">
                        {quizStarted && (
                            <button
                                className="bg-white text-blue-600 px-4 py-2 rounded"
                                onClick={() => setViewMode(viewMode === "single" ? "list" : "single")}
                            >
                                {viewMode === "single" ? "Switch to List View" : "Switch to Single View"}
                            </button>
                        )}
                        <ThemeToggle page="studentPage" />
                    </div>
                </div>

                {/* Mobile Button */}
                <div className="md:hidden fixed top-0 left-0 w-full bg-custom-blue p-4 flex justify-between items-center">
                    {quizStarted && <h3 className="text-white font-bold text-xl">
                        Time Left: {formatTime(timeLeft)}
                    </h3>}
                    <button
                        className="bg-white text-blue-600 px-4 py-2 rounded"
                        onClick={() => setMenuVisible(!isMenuVisible)}
                    >
                        {isMenuVisible ? "Close Menu" : "Menu"}
                    </button>
                </div>

                {/* Mobile Menu */}
                {isMenuVisible && (
                    <div className="fixed top-16 left-0 w-full bg-white shadow-md p-4 z-10" style={{
                        background: themeStyles.background,
                        color: themeStyles.text,
                    }}>
                        {/* Profile Picture */}
                        <div className="mb-4">
                            <ProfilePicture learnerName={learner.name} />
                        </div>

                        {/* Theme Toggle */}
                        <div>
                            <ThemeToggle />
                        </div>
                    </div>
                )}
            </div>

            {/* Scrollable Content Area */}
            {viewMode === 'single' && quizStarted && <div className="pt-[100px] px-10 md:pt-5 sm:pt-[100px]">
                <ProgressBar progress={quizProgress} />
            </div>}
            {quizStarted && <div className="flex overflow-y-auto p-4 justify-center">

                <div className='mx-5'>
                    {viewMode === 'single' ? (
                        // Single Question View
                        <div className="flex flex-col items-center">
                            <QuestionCard
                                questionNumber={currentIndex + 1}
                                question={questions[currentIndex].question}
                                options={questions[currentIndex].options}
                                selectedOption={answers[questions[currentIndex]._id]}
                                onSelect={(option) => handleSelect(option, questions[currentIndex]._id)}
                            />
                            <div className="mt-4 flex justify-between w-full">
                                <button
                                    disabled={currentIndex === 0}
                                    onClick={() => setCurrentIndex((prev) => prev - 1)}
                                    className={`px-4 py-2 rounded ${currentIndex === 0 ? 'bg-gray-400' : 'bg-custom-blue text-white'
                                        }`}
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => {
                                        if (currentIndex === questions.length - 1) navigate(`/result/${sessionId}`);
                                        else setCurrentIndex((prev) => prev + 1)
                                    }}
                                    className={`px-4 py-2 rounded ${currentIndex === questions.length - 1 ? 'bg-green-500 text-white' : 'bg-custom-blue text-white'
                                        }`}
                                >
                                    {currentIndex === questions.length - 1 ? 'Submit' : 'Next'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        // List View
                        <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                            {questions.map((q, index) => (
                                <QuestionCard
                                    key={q._id}
                                    questionNumber={index + 1}
                                    question={q.question}
                                    options={q.options}
                                    selectedOption={answers[q._id]}
                                    onSelect={(option) => handleSelect(option, q._id)}
                                />
                            ))}
                        </div>
                    )}

                </div>

            </div>}

            {!quizStarted && <div className='flex mt-10 overflow-y-auto'>
                <QuizInformation quiz={quizDetails} startQuiz={handleQuizStart} />
            </div>}

        </div>
    );
};

export default Quiz;
